// src/pages/FinancePage.js
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../utils/formatters';
import { financeService } from '../services/financeService';
import { 
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell
} from 'recharts';
import { 
    BanknotesIcon, ChartBarIcon, BriefcaseIcon, 
    PlusCircleIcon, TrashIcon, PencilSquareIcon,
    CalculatorIcon, ExclamationTriangleIcon,
    PaperClipIcon, EyeIcon
} from '@heroicons/react/24/solid';

// --- MODAL PARA GESTIONAR EGRESOS ---
const ExpenseModal = ({ expense, onClose, onSave, setNotification, activeJobs }) => {
    const isEditMode = !!expense;
    
    // Estados del Formulario
    const [folio, setFolio] = useState(expense?.id || '...');
    const [description, setDescription] = useState(expense?.description || '');
    const [amount, setAmount] = useState(expense?.amount || '');
    const [date, setDate] = useState(expense?.date?.seconds ? new Date(expense.date.seconds * 1000).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]);
    const [category, setCategory] = useState(expense?.category || expense?.type || 'Gasto Operacional');
    const [docType, setDocType] = useState(expense?.docType || (expense?.hasInvoice ? 'Factura' : 'Boleta'));
    const [invoiceNumber, setInvoiceNumber] = useState(expense?.invoiceNumber || '');
    const [provider, setProvider] = useState(expense?.provider || '');
    // eslint-disable-next-line no-unused-vars
    const [status] = useState(expense?.status || 'Pagado'); // Eliminado setStatus no usado
    const [relatedFolio, setRelatedFolio] = useState(expense?.relatedFolio || '');
    
    // Estados para archivo
    const [file, setFile] = useState(null);
    // eslint-disable-next-line no-unused-vars
    const [existingFileUrl] = useState(expense?.fileUrl || null); // Eliminado setExistingFileUrl no usado
    
    const [loading, setLoading] = useState(false);
    const folioGenerated = useRef(false);

    // Generar Folio al abrir (si es nuevo)
    useEffect(() => {
        const fetchId = async () => {
            if (isEditMode || folioGenerated.current) return;
            folioGenerated.current = true;
            try {
                const newId = await financeService.generateNextExpenseId();
                setFolio(newId);
            } catch (error) {
                setFolio("Error");
                setNotification({ message: 'Error generando folio.', type: 'error' });
            }
        };
        fetchId();
    }, [isEditMode, setNotification]);

    const handleSave = async (e) => {
        e.preventDefault();
        const finalAmount = parseFloat(amount);
        
        if (!description.trim() || isNaN(finalAmount) || finalAmount <= 0) {
            setNotification({ message: 'Complete descripción y monto válido.', type: 'error' }); 
            return;
        }
        if (docType === 'Factura' && (!invoiceNumber || !provider)) {
            setNotification({ message: 'Para Facturas, el proveedor y N° son obligatorios.', type: 'error' }); 
            return;
        }
        
        setLoading(true);
        
        const isFactura = docType === 'Factura';
        const isDeductible = docType !== 'Sin Comprobante';

        const expenseData = {
            id: folio, 
            description, 
            amount: finalAmount, 
            date, 
            category, 
            type: category, 
            docType,
            invoiceNumber: isFactura ? invoiceNumber : '', 
            provider: isFactura ? provider : '',
            hasInvoice: isFactura, 
            isDeductible: isDeductible, 
            status, 
            relatedFolio: relatedFolio || null,
            fileUrl: existingFileUrl, 
            storagePath: expense?.storagePath || null
        };

        try {
            await financeService.saveExpense(expenseData, file, isEditMode);
            setNotification({ message: `Egreso ${isEditMode ? 'actualizado' : 'registrado'} exitosamente.`, type: 'success' });
            onSave(); 
            onClose();
        } catch (error) {
            setNotification({ message: 'Error al guardar egreso.', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 dark:bg-black/80 flex items-center justify-center z-50 p-4 backdrop-blur-sm transition-colors">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-lg shadow-2xl overflow-y-auto max-h-[90vh] transition-colors">
                <h2 className="text-xl font-bold mb-4 text-gray-800 dark:text-gray-100 border-b dark:border-gray-700 pb-2 flex justify-between transition-colors">
                    <span>{isEditMode ? 'Editar Gasto' : 'Registrar Nuevo Gasto'}</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400 font-normal transition-colors">{folio}</span>
                </h2>
                <form onSubmit={handleSave} className="space-y-4">
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-100 dark:border-blue-800/50 transition-colors">
                        <label className="block text-xs font-bold text-blue-800 dark:text-blue-400 uppercase mb-1 transition-colors">Asignar a Trabajo</label>
                        <select value={relatedFolio} onChange={e => setRelatedFolio(e.target.value)} className="w-full rounded border-blue-300 dark:border-blue-700 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-blue-500 transition-colors">
                            <option value="">-- Gasto General de Empresa --</option>
                            {activeJobs.map(job => (<option key={job.id} value={job.id}>{job.id} - {job.clientName || 'Sin Cliente'}</option>))}
                        </select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                         <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors">Tipo Documento</label><select value={docType} onChange={(e) => setDocType(e.target.value)} className="mt-1 w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm transition-colors"><option value="Factura">Factura (IVA Crédito)</option><option value="Boleta">Boleta / Sueldo</option><option value="Sin Comprobante">Sin Comprobante</option></select></div>
                        <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors">Categoría</label><select value={category} onChange={(e) => setCategory(e.target.value)} className="mt-1 w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm transition-colors"><option value="Gasto Operacional">Gasto Operacional</option><option value="Compra Materiales">Compra Materiales</option><option value="Sueldos/Mano de Obra">Sueldos/Mano de Obra</option><option value="Arriendo Maquinaria">Arriendo Maquinaria</option><option value="Combustible/Traslado">Combustible/Traslado</option><option value="Gasto General/Admin">Gasto General/Admin</option></select></div>
                    </div>
                    <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors">Descripción</label><input type="text" value={description} onChange={(e) => setDescription(e.target.value)} className="mt-1 w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm transition-colors" required placeholder="Ej: Compra materiales" /></div>
                    <div className="grid grid-cols-2 gap-4">
                        <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors">Monto ($)</label><input type="text" inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^0-9]/g, ''))} className="mt-1 w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-white shadow-sm font-bold transition-colors" required /></div>
                        <div><label className="block text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors">Fecha</label><input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm transition-colors" required /></div>
                    </div>
                    
                    <div className="bg-gray-50 dark:bg-gray-700/50 p-3 rounded border border-gray-200 dark:border-gray-600 transition-colors">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center transition-colors">
                            <PaperClipIcon className="w-4 h-4 mr-1 text-gray-500 dark:text-gray-400 transition-colors"/>
                            Adjuntar Comprobante (PDF/Img)
                        </label>
                        <input 
                            type="file" 
                            accept="application/pdf, image/*"
                            onChange={(e) => setFile(e.target.files[0])}
                            className="block w-full text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-blue-50 dark:file:bg-blue-900/30 file:text-blue-700 dark:file:text-blue-400 hover:file:bg-blue-100 dark:hover:file:bg-blue-800/50 transition-colors"
                        />
                        {existingFileUrl && !file && (
                            <div className="mt-2 text-xs text-green-600 dark:text-green-400 flex items-center bg-green-50 dark:bg-green-900/30 p-1 rounded w-fit transition-colors">
                                <EyeIcon className="w-3 h-3 mr-1"/> Documento actual cargado
                            </div>
                        )}
                        {file && (
                            <div className="mt-2 text-xs text-blue-600 dark:text-blue-400 flex items-center transition-colors">
                                <span className="font-bold">Nuevo:</span> &nbsp; {file.name}
                            </div>
                        )}
                    </div>

                    {docType === 'Factura' && (
                        <div className="grid grid-cols-2 gap-4 pt-2 border-t dark:border-gray-700 bg-yellow-50 dark:bg-yellow-900/20 p-2 rounded animate-fade-in transition-colors">
                             <div><label className="block text-xs text-gray-600 dark:text-gray-400 font-bold transition-colors">Proveedor *</label><input type="text" value={provider} onChange={(e) => setProvider(e.target.value)} className="mt-1 w-full text-sm rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors" required /></div>
                            <div><label className="block text-xs text-gray-600 dark:text-gray-400 font-bold transition-colors">N° Factura *</label><input type="text" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} className="mt-1 w-full text-sm rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors" required /></div>
                        </div>
                    )}
                    <div className="flex justify-end gap-3 pt-4"><button type="button" onClick={onClose} className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition">Cancelar</button><button type="submit" disabled={loading} className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-bold shadow-md disabled:opacity-50">{loading ? 'Guardando...' : 'Guardar Gasto'}</button></div>
                </form>
            </div>
        </div>
    );
};

// --- COMPONENTE PRINCIPAL DE FINANZAS ---
const FinancePage = ({ setPage, setNotification }) => {
    const { user } = useAuth();
    const [allAccountingRecords, setAllAccountingRecords] = useState([]);
    const [allExpenses, setAllExpenses] = useState([]);
    const [parameters, setParameters] = useState({ iva: 19, ppm: 1.5 });
    const [loading, setLoading] = useState(true);
      
    const [currentTab, setCurrentTab] = useState('dashboard'); 
    const [showExpenseModal, setShowExpenseModal] = useState(false);
    const [editingExpense, setEditingExpense] = useState(null);
      
    const [selectedDate, setSelectedDate] = useState(new Date());
    const selectedMonth = selectedDate.getMonth();
    const selectedYear = selectedDate.getFullYear();

    // --- PERMISOS ---
    const isSuperUser = user?.role === 'superuser' || user?.email === 'nelson.jtapia@gmail.com';
    const isAdmin = user?.role === 'admin' || isSuperUser;
    const canManageFinance = isAdmin; 

    const goToPreviousMonth = () => setSelectedDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
    const goToNextMonth = () => setSelectedDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
    const isCurrentMonth = selectedYear === new Date().getFullYear() && selectedMonth === new Date().getMonth();

    const fetchData = useCallback(async () => {
         setLoading(true);
         try {
             // Usamos el servicio centralizado
             const { expenses, accountingRecords, parameters } = await financeService.getAllFinancialData();
             
             setAllExpenses(expenses);
             setAllAccountingRecords(accountingRecords);
             setParameters(parameters);

         } catch (error) {
             setNotification({ message: 'Error al cargar datos financieros.', type: 'error' });
         } finally {
             setLoading(false);
         }
    }, [setNotification]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleDeleteExpense = async (expense) => {
        if (!canManageFinance) return;
        if (!window.confirm("¿Eliminar este gasto? Esta acción no se puede deshacer y borrará los comprobantes adjuntos.")) return;
        try {
            await financeService.deleteExpense(expense);
            setNotification({ message: 'Gasto y comprobantes eliminados.', type: 'success' });
            fetchData();
        } catch (e) { 
            setNotification({ message: 'Error al eliminar.', type: 'error' }); 
        }
    };

    // --- HELPER DE FECHAS ---
    const isDateInSelectedMonth = useCallback((dateInput, monthStart, monthEnd) => {
        if (!dateInput) return false;
        let d;
        if (dateInput.seconds) d = new Date(dateInput.seconds * 1000);
        else if (dateInput instanceof Date) d = dateInput;
        else d = new Date(dateInput + "T12:00:00");
        return d >= monthStart && d <= monthEnd;
    }, []);

    // --- HELPER: MOTOR DE CÁLCULO FINANCIERO ---
    const calculateFinancials = useCallback((incomes, expenses, ivaPct, ppmPct) => {
        const ivaRate = ivaPct / 100;
        const ppmRate = ppmPct / 100;

        const totalVenta = incomes.reduce((sum, r) => sum + (parseFloat(r.totalAmount) || 0), 0); 
        const totalPagado = incomes.reduce((sum, r) => sum + (parseFloat(r.paidAmount) || 0), 0); 
        
        const netoVenta = Math.round(totalVenta / (1 + ivaRate));
        const ivaDebito = totalVenta - netoVenta;

        let totalGastoBruto = 0;
        let gastoNetoContable = 0;
        let ivaCredito = 0;
        // gastoNoContable eliminado porque no se usaba

        expenses.forEach(exp => {
            const amt = parseFloat(exp.amount) || 0;
            totalGastoBruto += amt;

            if (exp.docType === 'Factura' || exp.hasInvoice) {
                const neto = Math.round(amt / (1 + ivaRate));
                const iva = amt - neto;
                gastoNetoContable += neto;
                ivaCredito += iva;
            } else if (exp.docType === 'Boleta' || exp.docType === 'Sueldo') {
                gastoNetoContable += amt;
            } 
            // El else (gastoNoContable) se eliminó
        });

        const ivaPagar = Math.max(0, ivaDebito - ivaCredito);
        const utilidadContable = netoVenta - gastoNetoContable; 
        const ppmPagar = Math.max(0, Math.round(utilidadContable * ppmRate));

        const utilidadReal = totalVenta - totalGastoBruto - ivaPagar - ppmPagar;

        return {
            totalVenta, totalPagado, totalGastoBruto,
            netoVenta, ivaDebito, ivaCredito,
            ivaPagar, ppmPagar, utilidadContable,
            utilidadReal
        };
    }, []);

    // --- CÁLCULOS MENSUALES ---
    const dashboardData = useMemo(() => {
        const startOfMonth = new Date(selectedYear, selectedMonth, 1);
        const endOfMonth = new Date(selectedYear, selectedMonth + 1, 0, 23, 59, 59);

        const activeJobIds = new Set();
        let totalFacturadoMes = 0;
        let totalCajaMes = 0;

        // 1. Procesar Ingresos
        allAccountingRecords.forEach(record => {
            let recordHasActivity = false;

            // Facturas
            if (record.invoices && Array.isArray(record.invoices)) {
                record.invoices.forEach(inv => {
                    if (isDateInSelectedMonth(inv.date, startOfMonth, endOfMonth)) {
                        totalFacturadoMes += parseFloat(inv.amount) || 0;
                        recordHasActivity = true;
                    }
                });
            } else if (record.invoiceNumber && record.totalAmount) { 
                const dateToUse = record.visitDate || record.createdAt;
                if (isDateInSelectedMonth(dateToUse, startOfMonth, endOfMonth)) {
                    totalFacturadoMes += parseFloat(record.totalAmount) || 0;
                    recordHasActivity = true;
                }
            }

            // Pagos
            if (record.payments && Array.isArray(record.payments)) {
                record.payments.forEach(pay => {
                    if (isDateInSelectedMonth(pay.date, startOfMonth, endOfMonth)) {
                        totalCajaMes += parseFloat(pay.amount) || 0;
                        recordHasActivity = true;
                    }
                });
            }
            if (recordHasActivity) activeJobIds.add(record.id);
        });

        // 2. Gastos del Mes
        const currentMonthExpenses = allExpenses.filter(e => {
            const d = e.date?.seconds ? new Date(e.date.seconds * 1000) : new Date(e.date);
            return d >= startOfMonth && d <= endOfMonth;
        });

        const dummyIncomes = [{ totalAmount: totalFacturadoMes, paidAmount: totalCajaMes }];
        const financials = calculateFinancials(dummyIncomes, currentMonthExpenses, parameters.iva, parameters.ppm);

        const activeJobsList = allAccountingRecords.filter(r => activeJobIds.has(r.id));

        const monthlyChartData = [
            { name: 'Ingreso Caja', value: financials.totalPagado, fill: '#10B981' },
            { name: 'Facturado', value: financials.totalVenta, fill: '#3B82F6' },
            { name: 'Gastos Tot.', value: financials.totalGastoBruto, fill: '#EF4444' },
            { name: 'Utilidad Real', value: financials.utilidadReal, fill: '#8B5CF6' }
        ];

        return {
            activeJobsList,
            currentMonthExpenses,
            totalFacturado: totalFacturadoMes,
            totalPagadoClientes: totalCajaMes,
            ...financials,
            monthlyChartData
        };
    }, [selectedYear, selectedMonth, allAccountingRecords, allExpenses, parameters, isDateInSelectedMonth, calculateFinancials]);

    // --- CÁLCULOS ANUALES ---
    const annualChartData = useMemo(() => {
        return Array.from({ length: 12 }, (_, i) => {
            const mStart = new Date(selectedYear, i, 1);
            const mEnd = new Date(selectedYear, i + 1, 0, 23, 59, 59);
              
            let mIngresos = 0;
            allAccountingRecords.forEach(r => {
                 if(r.invoices) {
                     r.invoices.forEach(inv => { if(isDateInSelectedMonth(inv.date, mStart, mEnd)) mIngresos += parseFloat(inv.amount)||0; });
                 } else if (isDateInSelectedMonth(r.createdAt, mStart, mEnd)) { 
                     mIngresos += parseFloat(r.totalAmount)||0;
                 }
            });

            const mExpenses = allExpenses.filter(e => {
                const d = e.date?.seconds ? new Date(e.date.seconds * 1000) : new Date(e.date);
                return d >= mStart && d <= mEnd;
            });

            const fins = calculateFinancials([{ totalAmount: mIngresos, paidAmount: 0 }], mExpenses, parameters.iva, parameters.ppm);

            return {
                name: mStart.toLocaleString('es-CL', { month: 'short' }).toUpperCase(),
                Ingresos: fins.totalVenta,
                Egresos: fins.totalGastoBruto,
                Utilidad: fins.utilidadReal
            };
        });
    }, [selectedYear, allAccountingRecords, allExpenses, parameters, calculateFinancials, isDateInSelectedMonth]);

    // --- RENTABILIDAD POR TRABAJO ---
    const activeJobsProfitability = useMemo(() => {
        return dashboardData.activeJobsList.map(job => {
            const jobExpenses = allExpenses.filter(e => e.relatedFolio === job.id);
            const jobFinancials = calculateFinancials([job], jobExpenses, parameters.iva, parameters.ppm);
              
            const clientName = job.clientName || job.clientId || 'Sin Cliente'; 
            const margin = jobFinancials.totalVenta > 0 ? (jobFinancials.utilidadReal / jobFinancials.totalVenta) * 100 : 0;

            return { 
                ...job, 
                client: clientName, 
                expenses: jobFinancials.totalGastoBruto, 
                taxes: jobFinancials.ivaPagar + jobFinancials.ppmPagar,
                profit: jobFinancials.utilidadReal, 
                margin, 
                expensesCount: jobExpenses.length 
            };
        }).sort((a, b) => b.profit - a.profit);
    }, [dashboardData.activeJobsList, allExpenses, parameters, calculateFinancials]);

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center text-gray-500 dark:text-gray-400 transition-colors">Cargando datos financieros...</div>;
    }

    return (
        <div className="p-4 md:p-6 bg-gray-50 dark:bg-gray-900 min-h-screen transition-colors">
            {showExpenseModal && (
                <ExpenseModal 
                    expense={editingExpense} onClose={() => setShowExpenseModal(false)} onSave={fetchData} 
                    setNotification={setNotification} activeJobs={[...dashboardData.activeJobsList]} 
                />
            )}

            <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                <div className="flex items-center">
                    <button onClick={() => setPage('hub')} className="text-blue-600 dark:text-blue-400 hover:underline font-semibold mr-4 transition-colors">&larr; Volver</button>
                    <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100 uppercase tracking-wide transition-colors">CONTROL FINANCIERO</h1>
                </div>
                  
                <div className="flex bg-white dark:bg-gray-800 rounded-lg shadow p-1 transition-colors">
                    <button onClick={() => setCurrentTab('dashboard')} className={`px-4 py-2 rounded-md text-sm font-semibold transition ${currentTab === 'dashboard' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`}><ChartBarIcon className="w-4 h-4 inline mr-1"/> Flujo Mensual</button>
                    <button onClick={() => setCurrentTab('projects')} className={`px-4 py-2 rounded-md text-sm font-semibold transition ${currentTab === 'projects' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`}><BriefcaseIcon className="w-4 h-4 inline mr-1"/> Rentabilidad x Trabajo</button>
                    <button onClick={() => setCurrentTab('expenses')} className={`px-4 py-2 rounded-md text-sm font-semibold transition ${currentTab === 'expenses' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`}><BanknotesIcon className="w-4 h-4 inline mr-1"/> Lista Gastos</button>
                </div>
            </div>

            {/* SELECCIÓN DE MES */}
            <div className="flex justify-center items-center gap-4 bg-white dark:bg-gray-800 p-3 rounded-xl shadow-sm w-fit mx-auto mb-6 transition-colors">
                <button onClick={goToPreviousMonth} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full text-gray-600 dark:text-gray-300 transition-colors">&lt;</button>
                <span className="font-bold text-lg text-gray-800 dark:text-gray-100 w-40 text-center capitalize transition-colors">
                    {selectedDate.toLocaleString('es-CL', { month: 'long', year: 'numeric' })}
                </span>
                <button onClick={goToNextMonth} disabled={isCurrentMonth} className={`p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full text-gray-600 dark:text-gray-300 transition-colors ${isCurrentMonth ? 'opacity-30' : ''}`}>&gt;</button>
            </div>

            {/* --- VISTA 1: DASHBOARD MENSUAL --- */}
            {currentTab === 'dashboard' && (
                <div className="space-y-6 animate-fade-in">
                    
                    {/* TARJETAS RESUMEN */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow border-l-4 border-green-500 transition-colors">
                            <h3 className="text-gray-500 dark:text-gray-400 text-sm font-bold uppercase transition-colors">Ingresos Caja</h3>
                            <p className="text-3xl font-bold text-green-600 dark:text-green-400 mt-2 transition-colors">{formatCurrency(dashboardData.totalPagadoClientes)}</p>
                        </div>
                        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow border-l-4 border-blue-500 transition-colors">
                            <h3 className="text-gray-500 dark:text-gray-400 text-sm font-bold uppercase transition-colors">Facturado (Bruto)</h3>
                            <p className="text-3xl font-bold text-blue-600 dark:text-blue-400 mt-2 transition-colors">{formatCurrency(dashboardData.totalFacturado)}</p>
                        </div>
                        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow border-l-4 border-red-500 transition-colors">
                            <h3 className="text-gray-500 dark:text-gray-400 text-sm font-bold uppercase transition-colors">Gastos Totales</h3>
                            <p className="text-3xl font-bold text-red-600 dark:text-red-400 mt-2 transition-colors">{formatCurrency(dashboardData.totalGastoBruto)}</p>
                        </div>
                        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow border-l-4 border-purple-500 transition-colors">
                            <h3 className="text-gray-500 dark:text-gray-400 text-sm font-bold uppercase transition-colors">Utilidad Real</h3>
                            <p className={`text-3xl font-bold mt-2 transition-colors ${dashboardData.utilidadReal >= 0 ? 'text-purple-600 dark:text-purple-400' : 'text-red-600 dark:text-red-400'}`}>
                                {formatCurrency(dashboardData.utilidadReal)}
                            </p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 transition-colors">"Bolsillo"</p>
                        </div>
                    </div>

                    {/* IMPUESTOS */}
                    <div>
                        <h3 className="text-lg font-bold text-gray-700 dark:text-gray-100 mb-3 flex items-center transition-colors"><CalculatorIcon className="w-5 h-5 mr-2"/> Detalle de Impuestos (Mes)</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                             <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-blue-100 dark:border-blue-900/50 transition-colors">
                                <p className="text-gray-500 dark:text-gray-400 text-xs uppercase font-bold transition-colors">IVA Débito</p>
                                <p className="text-xl font-bold text-blue-600 dark:text-blue-400 mt-1 transition-colors">{formatCurrency(dashboardData.ivaDebito)}</p>
                            </div>
                            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-orange-100 dark:border-orange-900/50 transition-colors">
                                <p className="text-gray-500 dark:text-gray-400 text-xs uppercase font-bold transition-colors">IVA Crédito</p>
                                <p className="text-xl font-bold text-orange-600 dark:text-orange-400 mt-1 transition-colors">{formatCurrency(dashboardData.ivaCredito)}</p>
                            </div>
                            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-red-100 dark:border-red-900/50 transition-colors">
                                <p className="text-gray-500 dark:text-gray-400 text-xs uppercase font-bold transition-colors">IVA a Pagar</p>
                                <p className="text-xl font-bold text-red-600 dark:text-red-400 mt-1 transition-colors">{formatCurrency(dashboardData.ivaPagar)}</p>
                            </div>
                            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-purple-100 dark:border-purple-900/50 transition-colors">
                                <p className="text-gray-500 dark:text-gray-400 text-xs uppercase font-bold transition-colors">PPM a Pagar</p>
                                <p className="text-xl font-bold text-purple-600 dark:text-purple-400 mt-1 transition-colors">{formatCurrency(dashboardData.ppmPagar)}</p>
                            </div>
                        </div>
                    </div>

                    {/* GRÁFICOS */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* GRÁFICO MENSUAL */}
                        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow h-80 transition-colors">
                            <h3 className="text-gray-700 dark:text-gray-100 font-bold mb-4 transition-colors">Resumen Mensual</h3>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={dashboardData.monthlyChartData} layout="vertical" margin={{ top: 5, right: 30, left: 40, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                                    <XAxis type="number" tickFormatter={(val) => `$${val/1000}k`} domain={[0, 'auto']} allowDataOverflow={false}/>
                                    <YAxis type="category" dataKey="name" width={100} style={{fontSize: '11px', fontWeight: 'bold'}}/>
                                    <Tooltip formatter={(val) => formatCurrency(val)} cursor={{fill: 'transparent'}} />
                                    <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                                        {dashboardData.monthlyChartData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.fill} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>

                        {/* GRÁFICO ANUAL */}
                        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow h-80 transition-colors">
                            <h3 className="text-gray-700 dark:text-gray-100 font-bold mb-4 transition-colors">Evolución Anual {selectedYear}</h3>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={annualChartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis dataKey="name" style={{fontSize: '10px'}} />
                                    <YAxis tickFormatter={(val) => `$${val/1000}k`} domain={[0, 'auto']} />
                                    <Tooltip formatter={(val) => formatCurrency(val)} />
                                    <Legend />
                                    <Bar dataKey="Ingresos" fill="#3B82F6" name="Ingresos" />
                                    <Bar dataKey="Egresos" fill="#EF4444" name="Egresos" />
                                    <Bar dataKey="Utilidad" fill="#10B981" name="Utilidad Real" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
            )}

            {/* --- VISTA 2: RENTABILIDAD POR PROYECTO --- */}
            {currentTab === 'projects' && (
                <div className="space-y-8 animate-fade-in">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden transition-colors">
                        <div className="bg-gray-100 dark:bg-gray-700/50 px-6 py-3 border-b border-gray-200 dark:border-gray-700 font-bold text-gray-700 dark:text-gray-100 flex justify-between items-center transition-colors">
                            <span>Proyectos con Movimiento en {selectedDate.toLocaleString('es-CL', { month: 'long' })}</span>
                            <span className="text-xs font-normal bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400 px-2 py-1 rounded flex items-center transition-colors">
                                <ExclamationTriangleIcon className="w-3 h-3 mr-1"/> Rentabilidad Total del Proyecto
                            </span>
                        </div>
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 transition-colors">
                            <thead className="bg-gray-50 dark:bg-gray-700/50 transition-colors">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase transition-colors">Folio</th>
                                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase transition-colors">Cliente</th>
                                    <th className="px-6 py-3 text-right text-xs font-bold text-gray-500 dark:text-gray-400 uppercase transition-colors">Venta Total</th>
                                    <th className="px-6 py-3 text-right text-xs font-bold text-gray-500 dark:text-gray-400 uppercase transition-colors">Gastos Totales</th>
                                    <th className="px-6 py-3 text-right text-xs font-bold text-gray-500 dark:text-gray-400 uppercase transition-colors">Impuestos Proy.</th>
                                    <th className="px-6 py-3 text-right text-xs font-bold text-gray-500 dark:text-gray-400 uppercase transition-colors">Utilidad Real</th>
                                    <th className="px-6 py-3 text-center text-xs font-bold text-gray-500 dark:text-gray-400 uppercase transition-colors">% Margen</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800 transition-colors">
                                {activeJobsProfitability.map(job => (
                                    <tr key={job.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                        <td className="px-6 py-4 text-sm font-bold text-blue-600 dark:text-blue-400 transition-colors">{job.id}</td>
                                        <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300 transition-colors">{job.client}</td>
                                        <td className="px-6 py-4 text-sm text-right text-gray-900 dark:text-gray-100 transition-colors">{formatCurrency(job.totalAmount)}</td>
                                        <td className="px-6 py-4 text-sm text-right text-red-600 dark:text-red-400 transition-colors">{job.expenses > 0 ? `- ${formatCurrency(job.expenses)}` : '-'}</td>
                                        <td className="px-6 py-4 text-sm text-right text-orange-600 dark:text-orange-400 font-mono text-xs transition-colors">
                                            {formatCurrency(job.taxes)}
                                        </td>
                                        <td className={`px-6 py-4 text-sm text-right font-bold transition-colors ${job.profit > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>{formatCurrency(job.profit)}</td>
                                        <td className="px-6 py-4 text-center transition-colors">
                                            <span className={`px-2 py-1 rounded text-xs font-bold transition-colors ${job.margin > 30 ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400' : job.margin > 10 ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400' : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400'}`}>{job.margin.toFixed(1)}%</span>
                                        </td>
                                    </tr>
                                ))}
                                {activeJobsProfitability.length === 0 && <tr><td colSpan="7" className="p-8 text-center text-gray-400 dark:text-gray-500 transition-colors">No hubo movimiento financiero en proyectos este mes.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* --- VISTA 3: LISTA DE GASTOS --- */}
            {currentTab === 'expenses' && (
                <div className="space-y-8">
                    <div className="flex justify-end mb-4">
                        {/* BOTÓN PROTEGIDO: Solo Admins pueden crear gastos */}
                        {canManageFinance && (
                            <button onClick={() => { setEditingExpense(null); setShowExpenseModal(true); }} className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 font-semibold flex items-center shadow-md transition transform hover:scale-105">
                                <PlusCircleIcon className="w-5 h-5 mr-2"/> Registrar Gasto General
                            </button>
                        )}
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden transition-colors">
                        <div className="bg-gray-100 dark:bg-gray-700/50 px-6 py-3 border-b border-gray-200 dark:border-gray-700 font-bold text-gray-700 dark:text-gray-100 transition-colors">
                            Gastos del Mes: {selectedDate.toLocaleString('es-CL', { month: 'long' })}
                        </div>
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 transition-colors">
                            <thead className="bg-gray-100 dark:bg-gray-700/50 transition-colors">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase transition-colors">Fecha</th>
                                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase transition-colors">Descripción</th>
                                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase transition-colors">Tipo</th>
                                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase transition-colors">Doc</th>
                                    <th className="px-6 py-3 text-center text-xs font-bold text-gray-500 dark:text-gray-400 uppercase transition-colors">Comp.</th>
                                    <th className="px-6 py-3 text-right text-xs font-bold text-gray-500 dark:text-gray-400 uppercase transition-colors">Monto</th>
                                    <th className="px-6 py-3 text-center text-xs font-bold text-gray-500 dark:text-gray-400 uppercase transition-colors">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800 transition-colors">
                                {dashboardData.currentMonthExpenses.map(expense => (
                                    <tr key={expense.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                        <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400 transition-colors">{expense.date?.seconds ? new Date(expense.date.seconds * 1000).toLocaleDateString() : '-'}</td>
                                        <td className="px-6 py-4 text-sm text-gray-800 dark:text-gray-200 transition-colors">
                                            <span className="font-medium">{expense.description}</span>
                                            {expense.relatedFolio && <span className="block text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 w-fit px-2 py-0.5 rounded mt-1 transition-colors">Asoc. a: {expense.relatedFolio}</span>}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 transition-colors">{expense.category || expense.type}</td>
                                        <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 text-xs transition-colors">{expense.docType}</td>
                                         
                                        {/* COLUMNA COMPROBANTE CON ICONO DE OJO */}
                                        <td className="px-6 py-4 text-center">
                                            {expense.fileUrl ? (
                                                <a href={expense.fileUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-blue-500 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors bg-blue-50 dark:bg-blue-900/30 px-2 py-1 rounded-full text-xs font-bold border border-blue-200 dark:border-blue-800/50 hover:bg-blue-100 dark:hover:bg-blue-800/80" title="Ver Comprobante">
                                                    <EyeIcon className="w-3 h-3 mr-1" /> Ver
                                                </a>
                                            ) : (
                                                <span className="text-gray-300 dark:text-gray-600 text-xs transition-colors">-</span>
                                            )}
                                        </td>

                                        <td className="px-6 py-4 text-sm text-right font-bold text-red-600 dark:text-red-400 transition-colors">{formatCurrency(expense.amount)}</td>
                                        <td className="px-6 py-4 text-center space-x-2">
                                            {/* ACCIONES PROTEGIDAS: Solo Admins ven editar/borrar */}
                                            {canManageFinance && (
                                                <>
                                                    <button onClick={() => { setEditingExpense(expense); setShowExpenseModal(true); }} className="text-blue-500 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"><PencilSquareIcon className="w-5 h-5"/></button>
                                                    <button onClick={() => handleDeleteExpense(expense)} className="text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors"><TrashIcon className="w-5 h-5"/></button>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                {dashboardData.currentMonthExpenses.length === 0 && <tr><td colSpan="7" className="p-8 text-center text-gray-400 dark:text-gray-500 transition-colors">No hay gastos este mes.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FinancePage;